import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, AlertCircle, Eye, EyeOff } from "lucide-react";
import { getAdminUrl } from "@/lib/host";
import { PolicyAgreement } from "@/components/PolicyAgreement";

const Login = () => {
  const nav = useNavigate();
  const loc = useLocation();
  const { setSession, logout } = useAuth();
  const adminHost = false;

  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [rejected, setRejected] = useState(loc.state?.rejected === "not-admin");

  // OTP step (admin only)
  const [otpStep, setOtpStep] = useState(false);
  const [pendingId, setPendingId] = useState("");
  const [otpSentTo, setOtpSentTo] = useState("");
  const [otp, setOtp] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [resendTimer, setResendTimer] = useState(0);
  const [resending, setResending] = useState(false);
  const timerRef = useRef(null);
  const resendTimerRef = useRef(null);

  const redirectToAdmin = () => {
    if (adminHost) {
      nav("/admin");
      return;
    }
    window.location.assign(getAdminUrl());
  };

  const startTimer = (seconds) => {
    setOtpTimer(seconds);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setOtpTimer((t) => {
        if (t <= 1) { clearInterval(timerRef.current); return 0; }
        return t - 1;
      });
    }, 1000);
  };
  const startResendTimer = () => {
    setResendTimer(60);
    clearInterval(resendTimerRef.current);
    resendTimerRef.current = setInterval(() => {
      setResendTimer((seconds) => {
        if (seconds <= 1) {
          clearInterval(resendTimerRef.current);
          return 0;
        }
        return seconds - 1;
      });
    }, 1000);
  };
  useEffect(() => () => {
    clearInterval(timerRef.current);
    clearInterval(resendTimerRef.current);
  }, []);

  // If a customer account was rejected from admin host, keep the session signed out.
  useEffect(() => {
    if (rejected && logout) logout();
  }, [rejected, logout]);

  const submit = async (e) => {
    e.preventDefault();
    if (!adminHost && !consentAccepted) {
      toast.error("Please accept the Terms & Conditions and Privacy Policy to sign in.");
      return;
    }
    setLoading(true);
    setRejected(false);
    try {
      const { data } = await api.post("/auth/login", {
        email,
        password: pw,
        consent_accepted: consentAccepted,
      });
      if (data.otp_required) {
        setOtpStep(true);
        setPendingId(data.pending_id);
        setOtpSentTo(data.otp_sent_to);
        startTimer(data.expires_in || 600);
        startResendTimer();
        toast.success(`A 6-digit sign-in code was sent to ${data.otp_sent_to}`);
      } else {
        // On the admin host, refuse non-admin accounts up-front — never mount a customer session here.
        if (adminHost && data.user?.role !== "admin") {
          toast.error("This account does not have admin access.");
          setRejected(true);
          setLoading(false);
          return;
        }
        if (data.trusted_device) toast.success("Trusted device — no OTP needed");
        setSession(data);
        toast.success("Welcome back!");
        if (adminHost || data.user?.role === "admin") {
          redirectToAdmin();
        } else {
          nav("/");
        }
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { pending_id: pendingId, otp: otp.trim(), trust_device: trustDevice });
      setSession(data);
      toast.success(data.trusted_device ? "This device is now trusted for 14 days" : "Admin session verified");
      redirectToAdmin();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-otp", { pending_id: pendingId });
      startTimer(600);
      startResendTimer();
      toast.success("New code sent to your email");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setResending(false); }
  };

  const beginGoogleLogin = () => {
    if (!consentAccepted) {
      toast.error("Please accept the Terms & Conditions and Privacy Policy to continue.");
      return;
    }
    window.sessionStorage.setItem("eira-account-consent", "accepted");
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (otpStep) {
    const mm = String(Math.floor(otpTimer / 60)).padStart(2, "0");
    const ss = String(otpTimer % 60).padStart(2, "0");
    return (
      <div className="max-w-md mx-auto py-16 px-4">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-[#EBF2EE] text-[#2D5A47] flex items-center justify-center mx-auto mb-3">
            <ShieldCheck size={26} />
          </div>
          <h1 className="font-serif text-4xl">Enter your admin code</h1>
          <p className="text-sm text-[#64748B] mt-2">We sent a 6-digit code to <span className="font-semibold text-[#1E2A24]">{otpSentTo}</span>. Enter it below to unlock the Eira admin panel.</p>
        </div>
        <form onSubmit={verifyOtp} className="bg-white border border-[#E2DCD2] rounded-xl p-6 space-y-4">
          <input
            data-testid="otp-input"
            autoFocus
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            placeholder="123456"
            className="w-full border border-[#E2DCD2] rounded-lg px-3 py-4 text-center font-mono text-2xl tracking-[10px] outline-none focus:border-[#2D5A47]"
          />
          <div className="text-xs text-[#64748B] text-center">
            {otpTimer > 0 ? <>Code expires in <span className="font-mono font-semibold">{mm}:{ss}</span></> : "Code expired — request a new one below."}
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer bg-[#F4F1EA] border border-[#E2DCD2] rounded-lg px-3 py-2.5" data-testid="trust-device-label">
            <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} className="w-4 h-4 accent-[#2D5A47]" data-testid="trust-device-cb" />
            <span>Trust this device for <b>14 days</b> — skip the code on this browser</span>
          </label>
          <button type="submit" disabled={loading || otp.length !== 6} className="w-full bg-[#2D5A47] hover:bg-[#214435] disabled:opacity-50 text-white py-3 rounded-lg font-semibold" data-testid="otp-submit">
            {loading ? "Verifying…" : "Verify & sign in"}
          </button>
          <div className="flex items-center justify-between text-xs">
            <button type="button" onClick={() => { setOtpStep(false); setOtp(""); setPendingId(""); setOtpTimer(0); setResendTimer(0); clearInterval(timerRef.current); clearInterval(resendTimerRef.current); }} className="text-[#64748B] hover:text-[#1E2A24]">← Back to login</button>
            <button type="button" onClick={resendOtp} disabled={resending || resendTimer > 0} className="text-[#C86D42] font-semibold disabled:opacity-40 disabled:cursor-not-allowed" data-testid="otp-resend">
              {resending ? "Sending…" : resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "Resend OTP"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ---------- ADMIN HOST VARIANT ----------
  if (adminHost) {
    return (
      <div className="min-h-screen bg-[#1E2A24] flex items-center justify-center px-4 py-16" data-testid="admin-login-page">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 text-white">
            <div className="w-14 h-14 rounded-full bg-[#C86D42] flex items-center justify-center mx-auto mb-4">
              <ShieldCheck size={26} />
            </div>
            <div className="text-xs tracking-[0.3em] uppercase text-white/60 mb-2">Eira Admin</div>
            <h1 className="font-serif text-4xl font-semibold">Sign in to continue</h1>
            <p className="text-sm text-white/70 mt-2">
              Admin logins require a 6-digit email code every 14 days.
            </p>
          </div>
          {rejected && (
            <div className="mb-4 flex items-start gap-2 text-sm bg-red-500/10 border border-red-500/40 text-red-100 rounded-lg px-3 py-2.5" data-testid="admin-rejected">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <div>This account does not have admin access. Contact the site owner if you believe this is a mistake.</div>
            </div>
          )}
          <form onSubmit={submit} className="bg-white rounded-xl p-6 space-y-4">
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-[#8C6D46]">Admin email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full bg-white border border-[#E2DCD2] rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#2D5A47]" />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-[#8C6D46]">Password</label>
              <input data-testid="login-password" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 w-full bg-white border border-[#E2DCD2] rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#2D5A47]" />
            </div>
            <button disabled={loading} type="submit" className="w-full bg-[#2D5A47] hover:bg-[#214435] text-white font-semibold py-3 rounded-lg disabled:opacity-50" data-testid="login-submit">
              {loading ? "Signing in…" : "Continue"}
            </button>
            <div className="text-xs text-center text-[#8C6D46] bg-[#F4F1EA] rounded-lg px-3 py-2 flex items-center justify-center gap-1.5">
              <ShieldCheck size={12} /> A one-time code will be sent to your admin email.
            </div>
          </form>
          <div className="text-center mt-6">
            <a href="/" className="text-xs text-white/50 hover:text-white/80" data-testid="admin-go-store">← Go to eiraproducts.com</a>
          </div>
        </div>
      </div>
    );
  }

  // ---------- CUSTOMER HOST VARIANT ----------
  return (
    <div className="max-w-md mx-auto py-16 px-4" data-testid="login-page">
      <div className="text-center mb-8">
        <div className="text-xs tracking-[0.3em] uppercase text-[#8C6D46] mb-3">Welcome to Eira</div>
        <h1 className="font-serif text-4xl font-semibold">Sign in</h1>
      </div>
      <form onSubmit={submit} className="bg-white border border-[#E2DCD2] rounded-xl p-6 space-y-4">
        <div>
          <label className="text-sm font-medium">Email</label>
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full bg-white border border-[#E2DCD2] rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#2D5A47]" />
        </div>
        <div>
          <label className="text-sm font-medium">Password</label>
          <div className="relative mt-1">
            <input data-testid="login-password" type={showPassword ? "text" : "password"} required value={pw} onChange={(e) => setPw(e.target.value)} className="w-full bg-white border border-[#E2DCD2] rounded-lg px-4 py-2.5 pr-11 outline-none focus:ring-2 focus:ring-[#2D5A47]" />
            <button type="button" onClick={() => setShowPassword((show) => !show)} className="absolute inset-y-0 right-0 flex items-center px-3 text-[#64748B] hover:text-[#1E2A24]" data-testid="toggle-password-visibility" aria-label={showPassword ? "Hide password" : "Show password"}>
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <PolicyAgreement
          checked={consentAccepted}
          onChange={setConsentAccepted}
          testIdPrefix="login"
        />
        <button disabled={loading || !consentAccepted} type="submit" className="w-full bg-[#2D5A47] hover:bg-[#214435] text-white font-semibold py-3 rounded-lg disabled:opacity-50" data-testid="login-submit">
          {loading ? "Signing in…" : "Sign In"}
        </button>
        <div className="text-right text-xs -mt-2">
          <Link to="/forgot-password" className="text-[#C86D42] hover:underline font-medium" data-testid="forgot-password-link">Forgot password?</Link>
        </div>
        <div className="relative text-center text-xs text-[#64748B]">
          <span className="bg-white px-2 relative z-10">or</span>
          <div className="absolute inset-x-0 top-1/2 border-t border-[#E2DCD2]" />
        </div>
        <button
          type="button"
          onClick={beginGoogleLogin}
          disabled={!consentAccepted}
          className="w-full bg-white border border-[#E2DCD2] hover:border-[#2D5A47] py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="google-login"
        >
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" /><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" /><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" /><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" /></svg>
          Continue with Google
        </button>
        <div className="text-center text-sm text-[#64748B]">
          New to Eira? <Link to="/register" className="text-[#C86D42] font-medium">Register</Link>
        </div>
      </form>
    </div>
  );
};

export default Login;
